<script setup lang="ts">
import type { Leg, Price, TripType } from '@optionway/models';
import { breakpointsTailwind } from '@vueuse/core';
import { MoveRight } from 'lucide-vue-next';

const prefix = 'components.results.flightDetailsDialog';

const properties = defineProps<{
  legs?: Leg[];
  price?: Price;
  loading?: boolean;
  tripType?: TripType;
  hideBuyButton?: boolean;
  closeLabel?: string;
  initialLegId?: number;
}>();

const emit = defineEmits<{
  buy: [];
}>();

const open = defineModel<boolean>('open', { required: true });

const breakpoints = useBreakpoints(breakpointsTailwind);
const { t } = useI18n();
const locationLabels = useLocationLabels();
const isLg = breakpoints.greaterOrEqual('lg');

const averagePerPaxParts = computed(() => usePriceFormatParts(properties.price?.averagePerPax));

// t() and location labels cannot be called in a store --> translated display string exception. A function,
// not a computed: its branches would trip vue-mess-detector's simpleComputed.
const description = () => {
  const index = properties.legs?.findIndex((leg) => leg.id === properties.initialLegId) ?? -1;
  const leg = properties.legs?.[index];
  if (!leg) return t(`${prefix}.title`);
  const legWord = t(`${prefix}.leg`);
  const direction = index === 0 ? 'outbound' : 'inbound';
  const heading = properties.tripType === 'MULTI_DEST' ? `${legWord} ${index + 1}` : t(`${prefix}.${direction}`);
  const separator = t(`${prefix}.legLabelSeparator`);
  const routeTo = t(`${prefix}.routeTo`);
  return `${heading}${separator} ${locationLabels.city(leg.departure?.cityIataCode)} ${routeTo} ${locationLabels.city(leg.arrival?.cityIataCode)}`;
};
</script>

<template>
  <template v-if="isLg">
    <UiDialog v-model:open="open">
      <UiDialogContent class="flex max-h-sheet max-w-5xl flex-col gap-0 overflow-hidden p-0">
        <UiDialogHeader class="shrink-0 border-b px-6 py-5">
          <UiDialogTitle>{{ $t(`${prefix}.title`) }}</UiDialogTitle>
          <UiDialogDescription class="sr-only">{{ description() }}</UiDialogDescription>
        </UiDialogHeader>

        <div class="min-h-0 overflow-y-auto p-6">
          <ResultsFlightDetailsLegs
            v-if="legs"
            :legs="legs"
            :trip-type="properties.tripType"
            :initial-leg-id="properties.initialLegId"
          />
        </div>

        <div class="flex shrink-0 items-center justify-between border-t px-6 py-4">
          <div
            v-if="price && !hideBuyButton"
            class="flex flex-col gap-0.5"
          >
            <div class="inline-flex items-baseline gap-1.5">
              <span class="text-2xl font-semibold text-brand-primary-700">
                {{ averagePerPaxParts.integer }}<span class="text-base">{{ averagePerPaxParts.decimal }}</span>
              </span>
              <span class="text-sm font-normal text-text-secondary-600">
                {{ $t('components.results.priceSelect.perPaxFull') }}
              </span>
            </div>
            <span
              v-if="price.total !== price.averagePerPax"
              class="text-sm text-text-secondary-600"
            >
              {{ usePriceFormat(price.total) }} {{ $t('components.results.priceSelect.total') }}
            </span>
          </div>
          <div
            class="flex items-center gap-3"
            :class="{ 'ml-auto': hideBuyButton }"
          >
            <UiDialogClose as-child>
              <UiButton
                :variant="hideBuyButton ? 'primary' : 'secondary'"
                :color="hideBuyButton ? 'brand' : 'gray'"
                >{{ closeLabel ?? $t(`${prefix}.close`) }}</UiButton
              >
            </UiDialogClose>
            <UiButton
              v-if="!hideBuyButton"
              :loading="loading"
              :loading-label="$t('components.common.loading')"
              @click="emit('buy')"
            >
              {{ $t('components.results.priceSelect.select') }}
              <MoveRight
                class="h-4 w-4"
                aria-hidden="true"
              />
            </UiButton>
          </div>
        </div>
      </UiDialogContent>
    </UiDialog>
  </template>

  <template v-else>
    <UiCustomFullScreenSheet
      v-model:open="open"
      :title="$t(`${prefix}.title`)"
      :description="description()"
      :back-label="$t(`${prefix}.close`)"
    >
      <ResultsFlightDetailsLegs
        v-if="legs"
        :legs="legs"
        :trip-type="properties.tripType"
        :initial-leg-id="properties.initialLegId"
      />
      <UiSheetClose as-child>
        <UiButton
          variant="secondary"
          color="gray"
          class="mt-4 w-full"
        >
          {{ closeLabel ?? $t(`${prefix}.close`) }}
        </UiButton>
      </UiSheetClose>
    </UiCustomFullScreenSheet>
  </template>
</template>
